import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiDelete, apiGet, apiPost } from "@/lib/apiClient";
import { financeInvoiceBalance, formatFinanceMoney } from "@/lib/financeFormat";
import { useFinanceInvoicePaths } from "@/lib/financeRouteContext";
import {
  FINANCE_PAYMENT_METHODS,
  type FinanceInvoice,
  type FinancePayment,
  type FinancePaymentMethod,
} from "@/lib/financeTypes";
import { FinanceErrorBanner, financeStatusIsOpen } from "@/lib/financeUi";

type PaymentsResponse = { payments?: FinancePayment[] };
type InvoicesResponse = { invoices?: FinanceInvoice[] };

export function FinancePaymentsReceivedPage() {
  const [searchParams] = useSearchParams();
  const defaultInvoiceId = searchParams.get("invoiceId")?.trim() || undefined;
  const { detailPath, listPath: invoicesPath } = useFinanceInvoicePaths();

  const [rows, setRows] = useState<FinancePayment[]>([]);
  const [invoices, setInvoices] = useState<FinanceInvoice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(!!defaultInvoiceId);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [invoiceId, setInvoiceId] = useState(defaultInvoiceId || "");
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<FinancePaymentMethod>("bank_transfer");
  const [reference, setReference] = useState("");

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    const [pRes, iRes] = await Promise.all([
      apiGet<PaymentsResponse>("/api/finance/payments?limit=200"),
      apiGet<InvoicesResponse>("/api/finance/invoices?limit=200"),
    ]);
    if (!pRes.ok) {
      setError(pRes.error || "Could not load payments.");
      setRows([]);
    } else {
      setRows(pRes.data?.payments ?? []);
    }
    if (!iRes.ok) {
      setError((prev) => prev || iRes.error || "Could not load invoices.");
      setInvoices([]);
    } else {
      setInvoices(iRes.data?.invoices ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!defaultInvoiceId || !invoices.length) return;
    const inv = invoices.find((i) => i.id === defaultInvoiceId);
    if (inv && !amount) {
      const bal = financeInvoiceBalance(inv);
      if (bal > 0) setAmount(String(bal));
    }
  }, [defaultInvoiceId, invoices, amount]);

  const openInvoices = invoices.filter(
    (inv) => financeStatusIsOpen(inv.status) && financeInvoiceBalance(inv) > 0,
  );

  const onInvoiceChange = (id: string) => {
    setInvoiceId(id);
    const inv = invoices.find((i) => i.id === id);
    if (inv) {
      const bal = financeInvoiceBalance(inv);
      setAmount(bal > 0 ? String(bal) : "");
    }
  };

  const onCreate = async () => {
    setSaving(true);
    setError(null);
    const res = await apiPost<FinancePayment>("/api/finance/payments", {
      invoiceId,
      amount: Number(amount),
      paymentDate: paymentDate || null,
      method,
      reference: reference.trim() || null,
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Save failed.");
      return;
    }
    setDialogOpen(false);
    setInvoiceId(defaultInvoiceId || "");
    setAmount("");
    setReference("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setMethod("bank_transfer");
    await load();
  };

  const onConfirmDelete = async () => {
    if (!deleteId) return;
    setError(null);
    const res = await apiDelete<{ ok?: boolean }>(`/api/finance/payments/${encodeURIComponent(deleteId)}`);
    if (!res.ok) {
      setError(res.error || "Delete failed.");
      return;
    }
    setDeleteId(null);
    setDeleteOpen(false);
    await load();
  };

  const pickerInvoices =
    openInvoices.length > 0
      ? openInvoices
      : defaultInvoiceId
        ? invoices.filter((i) => i.id === defaultInvoiceId)
        : [];

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">Payments received</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Record payments against open invoices. Path: account → invoice → payment.
        </p>
      </div>

      {error ? <FinanceErrorBanner message={error} /> : null}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Link
          to={invoicesPath}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
        >
          Invoices
        </Link>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          disabled={openInvoices.length === 0 && !defaultInvoiceId}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          Record payment
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
            <tr>
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">Invoice</th>
              <th className="px-4 py-3 font-semibold">Account</th>
              <th className="px-4 py-3 font-semibold">Amount</th>
              <th className="px-4 py-3 font-semibold">Method</th>
              <th className="px-4 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  Loading payments…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  No payments recorded yet. Create an account and invoice, then record a payment here.
                </td>
              </tr>
            ) : (
              rows.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 dark:border-white/5">
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{p.paymentDate || "—"}</td>
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={detailPath(p.invoiceId)}
                      className="text-indigo-600 hover:underline dark:text-indigo-300"
                    >
                      {p.invoiceNumber || p.invoiceId}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{p.customerName?.trim() || "—"}</td>
                  <td className="px-4 py-3 tabular-nums text-slate-600 dark:text-slate-300">
                    {formatFinanceMoney(p.amount, p.currency || "USD")}
                  </td>
                  <td className="px-4 py-3 capitalize text-slate-600 dark:text-slate-300">{p.method || "other"}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteId(p.id);
                        setDeleteOpen(true);
                      }}
                      className="rounded-md px-2 py-1 text-sm font-medium text-rose-700 hover:bg-rose-50 dark:text-rose-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record payment</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Invoice</span>
              <select
                value={invoiceId}
                onChange={(e) => onInvoiceChange(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              >
                <option value="">Select open invoice</option>
                {pickerInvoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.number || inv.id} — {inv.customerName || "No account"} (
                    {formatFinanceMoney(financeInvoiceBalance(inv), inv.currency || "USD")} due)
                  </option>
                ))}
              </select>
              {openInvoices.length === 0 && !defaultInvoiceId ? (
                <p className="mt-1 text-xs text-slate-500">
                  No open invoices.{" "}
                  <Link to="/finance/sales/invoices/new" className="text-indigo-600 hover:underline dark:text-indigo-300">
                    Create an invoice
                  </Link>{" "}
                  first.
                </p>
              ) : null}
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Amount</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Payment date</span>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Method</span>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value as FinancePaymentMethod)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              >
                {FINANCE_PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Reference</span>
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Check #, transfer id…"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDialogOpen(false)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onCreate()}
              disabled={saving || !invoiceId || !amount || Number(amount) <= 0}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Record"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete payment?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            This updates the related invoice balance and status. This cannot be undone.
          </p>
          <DialogFooter>
            <button
              type="button"
              onClick={() => {
                setDeleteOpen(false);
                setDeleteId(null);
              }}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onConfirmDelete()}
              className="rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-500"
            >
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
